import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, computed, inject, OnInit, PLATFORM_ID, Renderer2, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router, ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from 'src/app/shared/config/config.service';
import { BffService } from 'src/app/shared/services/bff.service';
import { LogRocketService } from 'src/app/shared/services/logrocket.service';
import { VideoDataService } from 'src/app/shared/services/video-data.service';
import { VideoIppResponse } from 'src/app/shared/types/video-ipp-response.type';
import { ErrorDialogComponent } from '../components/error-dialog/error-dialog.component';
import { InfoCardComponent } from '../components/info-card/info-card.component';

interface SmartPlayer {
  initIndivideo(data: VideoIppResponse | null): void;
  play?(): void;
  pause?(): void;
}

declare global {
  interface Window {
    onIndivideoReady?: (smartPlayer: SmartPlayer) => void;
    individeoData?: VideoIppResponse | null;
  }
}

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatCardModule, MatIconModule, InfoCardComponent],
})
export class LandingPageComponent implements OnInit {
  private readonly renderer = inject(Renderer2);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly videoDataService = inject(VideoDataService);
  private readonly configService = inject(ConfigService);
  private readonly dialog = inject(MatDialog);
  private readonly bffService = inject(BffService);
  private readonly logRocketService = inject(LogRocketService);

  private readonly feedbackWidgetClosed = signal(false);
  readonly customerName = computed(() => this.videoDataService.getVideoData()?.nombre ?? '');
  readonly surveyUrl = computed(() => this.videoDataService.getVideoData()?.URLencuesta ?? null);
  readonly showFeedbackWidget = computed(() => !!this.surveyUrl() && !this.feedbackWidgetClosed());
  readonly expandedCards = signal<Set<number>>(new Set());
  readonly videoLoaded = signal(false);
  readonly videoReady = signal(false);
  readonly errorMessage = signal<string>('');
  readonly isDownloading = signal(false);

  readonly thumbnailTextDate = computed(() => this.videoThumbnailTextDate());

  private smartPlayerInstance: SmartPlayer | null = null;

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.logRocketService.init();
      this.initializeIndiVideo();
    }
  }

  private videoThumbnailTextDate(): string {
    const videoDate = new Date();
    videoDate.setMonth(videoDate.getMonth() - 1);

    const formatter = new Intl.DateTimeFormat('es-ES', {
      month: 'long',
      year: 'numeric',
    });

    const result = formatter.format(videoDate);
    return result.charAt(0).toUpperCase() + result.slice(1);
  }

  private initializeIndiVideo(): void {
    window.onIndivideoReady = (smartPlayer: SmartPlayer) => {
      this.onVideoReady(smartPlayer);
    };

    this.loadIndiVideoScript();
  }

  private loadIndiVideoScript(): void {
    const script = this.renderer.createElement('script');
    script.src = `${this.configService.INDIVIDEO_URL}`;
    script.dataset.ivOnReady = 'onIndivideoReady';
    script.dataset.ivAutoInit = 'false';
    script.dataset.ivAttachmentCode = this.configService.INDIVIDEO_DEPLOYMENT_CODE;
    script.dataset.ivLang = 'es-CL';
    script.dataset.ivUseBranding = 'false';
    script.dataset.ivAutoFullscreenOnFirstPlayMobile = 'true';
    script.dataset.ivAutoplay = 'true';
    script.dataset.ivStrictAutoplay = 'true';

    const videoContainer = document.getElementById('individeo-container');
    if (videoContainer) {
      this.renderer.appendChild(videoContainer, script);
    }
  }

  private async onVideoReady(smartPlayer: any): Promise<void> {
    try {
      const videoData = this.videoDataService.getVideoData();

      if (!videoData) {
        throw new Error('No se encontraron datos del video. Por favor inicia sesión nuevamente.');
      }

      this.smartPlayerInstance = smartPlayer;
      smartPlayer.initIndivideo(videoData);
      this.videoLoaded.set(true);
      this.videoReady.set(true);
    } catch (error) {
      let message = 'No se pudo cargar el video personalizado';

      if (error instanceof Error) {
        message = error.message;
      }

      this.dialog.open(ErrorDialogComponent, {
        width: '600px',
        panelClass: 'custom-dialog-rounded',
        data: {
          message: message,
        },
      });

      setTimeout(() => {
        const id = this.route.snapshot.queryParamMap.get('id');
        this.router.navigate(['/login'], id ? { queryParams: { id } } : {});
      }, 2000);
    }
  }

  closeFeedbackWidget(): void {
    this.feedbackWidgetClosed.set(true);
  }

  async onDownloadClick(): Promise<void> {
    const rut = this.videoDataService.getRut();
    const id = this.route.snapshot.queryParamMap.get('id');
    const periodo = this.videoDataService.getVideoData()?.periodo?.toString() ?? '';

    if (!rut || !id) {
      this.dialog.open(ErrorDialogComponent, {
        width: '600px',
        panelClass: 'custom-dialog-rounded',
        data: { message: 'No se pudo obtener los datos necesarios para descargar el informe.' },
      });
      return;
    }

    this.isDownloading.set(true);
    try {
      const response = await firstValueFrom(this.bffService.downloadInforme(rut, id));
      const linkSource = `data:application/pdf;base64,${response.pdf_base64}`;
      const downloadLink = document.createElement('a');
      downloadLink.href = linkSource;
      downloadLink.download = response.filename || `IPP_${rut}_${periodo}.pdf`;
      downloadLink.click();
    } catch {
      this.dialog.open(ErrorDialogComponent, {
        width: '600px',
        panelClass: 'custom-dialog-rounded',
        data: { message: 'No se pudo descargar el informe. Por favor intenta nuevamente.' },
      });
    } finally {
      this.isDownloading.set(false);
    }
  }

  toggleCard(index: number): void {
    const current = this.expandedCards();
    const updated = new Set(current);

    if (updated.has(index)) {
      updated.delete(index);
    } else {
      updated.add(index);
    }

    this.expandedCards.set(updated);
  }

  isCardExpanded(index: number): boolean {
    return this.expandedCards().has(index);
  }
}
